import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

type ShadowContentRendererProps = {
    htmlContent: string;
}

/**
 * This component, `ShadowContentRenderer`, renders provided HTML content 
 * within a Shadow DOM. This approach encapsulates the HTML and its 
 * associated CSS, preventing styles from leaking out and affecting the 
 * rest of the application. It also sanitizes the HTML using DOMPurify to 
 * mitigate XSS risks, making it a safer alternative to 
 * using `dangerouslySetInnerHTML`.
 */
const ShadowContentRenderer = ({ htmlContent }: ShadowContentRendererProps) => {
    const hostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hostRef.current && htmlContent) {
            if (!hostRef.current.shadowRoot) {
                const shadowRoot = hostRef.current.attachShadow({ mode: 'open' });
                const sanitizedHtml = DOMPurify.sanitize(htmlContent);
                shadowRoot.innerHTML = sanitizedHtml;
            } else {
                const sanitizedHtml = DOMPurify.sanitize(htmlContent);
                hostRef.current.shadowRoot.innerHTML = sanitizedHtml;
            }
        }
    }, [htmlContent]);

    // Renders the host element for the shadow root
    return <div ref={hostRef}/>;
};

export default ShadowContentRenderer;